import React from 'react';
import { withTranslation } from 'react-i18next';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { dateFromMYSQLTimeStamp } from '../../../services/util/dateUtil';
import VMUtil from '../../../services/util/vmUtil';
import EventListCardToolTip from '../../Dashboard/CardHandling/EventListCard/ToolTip/EventListTooltip';
import Popover from 'react-bootstrap/Popover';
import TableToolBar from '../../../components/SalesAndInomeCard/SalesAndIncomeTable/TableToolBar';
import classes from '../LogsCard/LogsCard.module.css';
import Spinner from '../../../components/UI/Spinner/Spinner';
import './Logstable.css';

const LogsTable = props => {
	const templateResolver = (templateString, templateVars) => {
		return new Function('return `' + templateString + '`;').call(templateVars);
	};

	const renderMalfunctionTooltip = (params, title, machineType) => {
		try {
			const parsedValues = JSON.parse(params.m);
			if (
				!parsedValues ||
				parsedValues.length <= 0 ||
				!parsedValues[0].display
			) {
				throw new Error('Incorrect error message');
			}
			return (
				<Tooltip
					style={{
						borderRadius: '10px',
						padding: '0px',
						overflow: 'hidden',
						borderColor: 'transparent',
					}}
				>
					<EventListCardToolTip screens={parsedValues} machineType={machineType ?? props.machine.machineType} />
				</Tooltip>
			);
		} catch (error) {
			return (
				<Tooltip
					style={{
						borderRadius: '10px',
						padding: '0px',
						overflow: 'hidden',
						borderColor: 'transparent',
					}}
				>
					<span>{params.m ? params.m : title}</span>		
				</Tooltip>
			);
		}
	};

	const renderBasicTooltip = (mappedParam, label, title) => {
		return (
			<Tooltip style={{ borderRadius: '10px', padding: '10px', backgroundColor: '#000', color: '#fff', borderColor: '#000' }} id={'test'}>
				{new Function('return `' + label + '`;').call(mappedParam)}
			</Tooltip>
		);
	};

	const getStatusBadge = cell => {
		return props.getStatusBadge(cell);
	};

	const hasSameParams = (leftParams, rightParams) => {
		return (
			JSON.stringify(leftParams || []) == JSON.stringify(rightParams || [])
		);
	};

	const areInSameAggregateGroup = (leftRow, rightRow) => {
		if (!leftRow || !rightRow) return false;

		return (
			leftRow.eventType == rightRow.eventType &&
			leftRow.machineId == rightRow.machineId &&
			leftRow.machineName == rightRow.machineName &&
			leftRow.machineType == rightRow.machineType &&
			hasSameParams(leftRow.params, rightRow.params)
		);
	};

	const aggregateConsecutiveEvents = events => {
		;
		if (!events || events.length <= 0) return [];

		const getDateValueForSort = eventRow => {
			if (!eventRow || !eventRow.date) return 0;

			const directParsedValue = Date.parse(
				String(eventRow.date).replace(' ', 'T')
			);
			if (!isNaN(directParsedValue)) return directParsedValue;

			try {
				return dateFromMYSQLTimeStamp(eventRow.date).getTime();
			} catch (error) {
				return 0;
			}
		};

		const sortedEvents = [...events].sort((leftEvent, rightEvent) => {
			const rightDateValue = getDateValueForSort(rightEvent);
			const leftDateValue = getDateValueForSort(leftEvent);
			if (rightDateValue === leftDateValue) {
				return 0;
			}
			return rightDateValue - leftDateValue;
		});

		const aggregatedRows = [];
		sortedEvents.forEach((eventRow, position) => {
			const previousRow = aggregatedRows[aggregatedRows.length - 1];
			if (previousRow && areInSameAggregateGroup(previousRow, eventRow)) {
				previousRow.aggregatedCount += 1;
				previousRow.aggregatedDates.push(eventRow.date);
				return;
			}

			aggregatedRows.push({
				...eventRow,
				aggregatedCount: 1,
				aggregatedDates: [eventRow.date],
				index: `${eventRow.index || position}-${position}`
			});
		});

		return aggregatedRows;
	};

	const getEventLabel = (cell, row) => {
		const eventType = props.eventTypeSeriesSettings.find(
			curEventType => curEventType.typeId === cell
		);
		const label = eventType ? eventType.label : '';
		return <strong>{label}</strong>;
	};

	const getEventLabelWithCount = (cell, row) => {
		const eventType = props.eventTypeSeriesSettings.find(
			curEventType => curEventType.typeId === cell
		);
		const label = eventType ? eventType.label : '';
		const occurrenceCount = row.aggregatedCount || 1;
		const occurrenceTooltip = (
			<Tooltip
				id={`event-aggregate-${row.index}`}
				style={{
					borderRadius: '10px',
					padding: '10px',
					backgroundColor: '#000',
					color: '#fff',
					borderColor: '#000',
					maxWidth: '360px'
				}}
			>
				{(row.aggregatedDates || []).map((dateValue, index) => (
					<div key={`${row.index}-date-${index}`}>{formatTooltipDate(dateValue)}</div>
				))}
			</Tooltip>
		);

		return (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: '8px'
				}}
			>
				<strong>{label}</strong>
				{occurrenceCount > 1 ? <strong>x{occurrenceCount}</strong> : null}
				{occurrenceCount > 1 ? (
					<OverlayTrigger
						placement="right"
						overlay={occurrenceTooltip}
						trigger={['hover', 'focus']}
						rootClose
						transition={null}
					>
						<Badge className={classes.IBadge}>
							<i className="fa fa-info-circle" style={{ padding: '2px', margin: 'auto' }}></i>
						</Badge>
					</OverlayTrigger>
				) : null}
			</div>
		);
	};

	const getEventStringFromEventType = (eventTypeId, params, row) => {
		const eventType = props.eventTypeSeriesSettings.find(
			curEventType => curEventType.typeId === eventTypeId
		);
		let mappedParam = {};
		params.forEach(param => {
			mappedParam[param.parameterName] = param.parameterValue;
		});
		const label = eventType ? eventType.text : '';
		const title = eventType ? eventType.label : '';
		const id = eventType ? eventType.typeId : '';
		try {			
			const resolvedText = templateResolver(label, mappedParam);
			let result = resolvedText;
			const eventsWithMessage = [3, 22, 23, 25];
			if (
				(eventsWithMessage.includes(id)) &&
				(mappedParam.m && mappedParam.m.length > 0)
			) {
				const tooltip = renderMalfunctionTooltip(mappedParam, title, row.machineType);
				result = (
					<OverlayTrigger
						placement="right"
						overlay={tooltip}
						trigger="click"
						rootClose
						transition={null}
					>
						<Badge
							className={id == 3 || id == 22 || (id == 25 && mappedParam.m && mappedParam.m.length > 0) ? classes.MFBadge : classes.IBadge}
							style={{backgroundColor: "transparent"}}
						>							
							<i
								className="fa fa-book"
								style={{ padding: '2px', margin: 'auto' }}
							></i>
						</Badge>
					</OverlayTrigger>
				);
			} else {
				const basicEvents = [0, 1, 4, 5, 6, 7, 16];
				if (
					basicEvents.includes(id)
				) {
					const tooltip = renderBasicTooltip(mappedParam, label, title);
					result = (
						<OverlayTrigger
							placement="right"
							overlay={tooltip}
							trigger="click"
							rootClose
							transition={null}
						>
							<Badge className={classes.IBadge}>
								<i
									className="fa fa-info-circle"
									style={{ padding: '2px', margin: 'auto' }}
								></i>
							</Badge>
						</OverlayTrigger>
					);
				} else {
					result = <></>;
				}
			}
			return <>{result}</>;
		} catch (error) {
			console.error('Could not resolve text: ' + label);
			return '';
		}
	};

	const dateFormatter = (cell, row) => {
		if (cell) {
			const dateFromTimeStamp = dateFromMYSQLTimeStamp(cell);
			const stringRep =
				dateFromTimeStamp.toLocaleDateString() +
				' ' +
				dateFromTimeStamp.toLocaleTimeString();

			return stringRep;
		} else {
			return cell;
		}
	};

	const formatTooltipDate = date => {
		if (!date) return '';
		const convertedDate = dateFromMYSQLTimeStamp(date);
		return (
			convertedDate.toLocaleDateString() +
			' ' +
			convertedDate.toLocaleTimeString()
		);
	};

	const aggregateInfoFormatter = (cell, row) => {
		return (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}
			>
				{getStatusBadge(cell)}
			</div>
		);
	};

	const eventTypeFormatter = (cell, row) => {
		return getEventStringFromEventType(cell, row.params, row);
	};

	const onRowClik = row => {
		props.history.push('vms/' + VMUtil.encodeVMID(row.machineId) + '/detailer');
	};

	const editFormatter = (cell, row) => {
		return (
			<>
				<Button
					onClick={e => onRowClik(row)}
					variant="success"
					className={classes.EditButton}
				>
					<i className="fa fa-external-link" />
				</Button>
			</>
		);
	};
	const createCustomToolBar = () => {
		return (
			<TableToolBar
				startDate={props.startDate}
				endDate={props.endDate}
				btnGroup={props.filterButton}
				onInputChange={props.onDateChange}
				loadButton={props.loadButton}
			/>
		);
	};
	const tableOptions = {
		hideSizePerPage: false,
		paginationSize: 3,
		sizePerPageList: [10],
		sizePerPage: props.recordPerPage || 100,
		withFirstAndLast: true,
		alwaysShowAllBtns: true,
		pageStartIndex: 1,
		paginationPosition: 'bottom',
		alwaysShowAllBtns: false,
		clearSearch: true,
		hidePageListOnlyOnePage: true,
		sortName: 'date',
		sortOrder: 'desc',
		toolBar: createCustomToolBar
	};

	const aggregatedData = aggregateConsecutiveEvents(props.data || []);
	
	return props.loading ? (
		<Spinner />
	) : (
		<>
			<BootstrapTable
				data={aggregatedData}
				version="4"
				striped
				hover
				pagination={!props.hidePager}
				insertRow={false}
				options={tableOptions}
				multiColumnSearch
				condensed
				bordered={false}
				dataAlign={'center'}
				size="sm"
				key="index"
				headerContainerClass={classes.HeaderContainerClass}
				tableHeaderClass={classes.TableHeaderClass}
			>
				<TableHeaderColumn
					isKey
					width="1"
					dataAlign="center"
					dataField="index"
					key="index"
					hidden
					thStyle={{
						borderColor: 'transparent'
					}}
					tdStyle={{ paddingTop: '15px' }}
				>
					{'Index'}
				</TableHeaderColumn>

				<TableHeaderColumn
					width="100"
					dataAlign="center"
					dataField="eventType"
					dataFormat={aggregateInfoFormatter}
					thStyle={{
						borderColor: 'transparent'
					}}
					tdStyle={{ paddingTop: '10px' }}
				/>

				<TableHeaderColumn
					width="250"
					dataAlign="left"
					dataField="eventType"
					dataFormat={getEventLabelWithCount}
					thStyle={{
						borderColor: 'transparent'
					}}
					tdStyle={{ paddingTop: '15px' }}
					dataSort
				>
					{props.t('eventList.table.eventType')}
				</TableHeaderColumn>

				<TableHeaderColumn
					width="250"
					dataAlign="left"
					dataField="date"
					dataFormat={dateFormatter}
					thStyle={{
						borderColor: 'transparent'
					}}
					tdStyle={{ paddingTop: '15px' }}
					dataSort
				>
					{props.t('eventList.table.date')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="250"
					dataAlign="left"
					dataField="machineName"
					thStyle={{
						borderColor: 'transparent'
					}}
					tdStyle={{ paddingTop: '15px' }}
					dataSort
				>
					{props.t('eventList.table.machine')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="300"
					dataAlign="left"
					dataField="eventType"
					dataFormat={eventTypeFormatter}
					thStyle={{
						borderColor: 'transparent'
					}}
					tdStyle={{ paddingTop: '10px' }}
				>
					{props.t('eventList.table.details')}
				</TableHeaderColumn>
				<TableHeaderColumn
					width="200"
					dataAlign="right"
					dataField="userID"
					dataFormat={editFormatter}
					thStyle={{
						borderColor: 'transparent'
					}}
					hidden={props.hideLink}
				>
					{props.t('eventList.table.machineDetails')}
				</TableHeaderColumn>
			</BootstrapTable>
		</>
	);
};

export default withTranslation()(LogsTable);
